import React from "react";

interface Field {
    label: string;
    colSpan: number;
    type?: "date" | "checkbox" | "boolean";
    options?: string[]; // For checkbox fields, this will contain the list of options
}

export interface PrintViewSectionProps {
    title: string;
    fields: Field[];
    children?: React.ReactNode[];
}

export const PrintViewSection: React.FC<PrintViewSectionProps> = ({
    title,
    fields,
    children,
}) => {
    // Calculate total columns (max 12)
    const totalColumns = Math.min(
        12,
        fields.reduce((sum, field) => sum + field.colSpan, 0),
    );

    // Create grid template columns
    const gridTemplateColumns = `repeat(${totalColumns}, 1fr)`;

    // Calculate which fields are in the last row and which are last in their row
    let currentRowWidth = 0;
    const lastRowStartIndex = fields.reduce((lastIndex, field, index) => {
        currentRowWidth += field.colSpan;
        if (currentRowWidth >= totalColumns) {
            currentRowWidth = 0;
            return index + 1;
        }
        return lastIndex;
    }, 0);

    // Calculate which fields are last in their row
    currentRowWidth = 0;
    const isLastInRow = fields.map((field, index) => {
        currentRowWidth += field.colSpan;
        const isLast = currentRowWidth >= totalColumns;
        if (isLast) {
            currentRowWidth = 0;
        }
        return isLast;
    });

    return (
        <div>
            <div
                className="w-full border border-black"
                style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                {/* Title row */}
                <div
                    className="border-b border-black p-2 font-semibold text-center bg-blue-100"
                    style={{ gridColumn: `1 / span ${totalColumns}` }}>
                    {title}
                </div>

                {/* Fields row */}
                <div className="grid" style={{ gridTemplateColumns }}>
                    {fields.map((field, index) => (
                        <div
                            key={index}
                            className={`border-black p-2 min-h-[50px] relative ${
                                index >= lastRowStartIndex ? "" : "border-b"
                            } ${isLastInRow[index] ? "" : "border-r"}`}
                            style={{ gridColumn: `span ${field.colSpan}` }}>
                            <div className="text-[12px] text-gray-500 absolute top-1 left-2">
                                {field.label}
                            </div>
                            {field.type === "date" && (
                                <div className="mt-6 h-[35px] flex flex-row">
                                    <div className="border-r border-black flex-1 relative">
                                        <div className="text-[8px] text-gray-500 absolute bottom-1 left-0 right-0 text-center">
                                            MM
                                        </div>
                                    </div>
                                    <div className="border-r border-black flex-1 relative">
                                        <div className="text-[8px] text-gray-500 absolute bottom-1 left-0 right-0 text-center">
                                            DD
                                        </div>
                                    </div>
                                    <div className="border-black flex-1 relative">
                                        <div className="text-[8px] text-gray-500 absolute bottom-1 left-0 right-0 text-center">
                                            YYYY
                                        </div>
                                    </div>
                                </div>
                            )}
                            {field.type === "checkbox" && field.options && (
                                <div className="mt-6 space-y-2">
                                    {field.options.map((option, i) => (
                                        <div key={i} className="flex items-start">
                                            <div className="w-4 aspect-square border border-black mr-2 flex-shrink-0 mt-[2px]"></div>
                                            <span className="text-xs leading-snug">
                                                {field.label}: {option}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {field.type === "boolean" && (
                                <div className="mt-6 flex items-center gap-4 flex-wrap">
                                    <div className="flex items-start flex-0">
                                        <div className="w-4 aspect-square border border-black mr-2 flex-shrink-0 mt-[2px]"></div>
                                        <span className="text-xs leading-snug whitespace-nowrap">
                                            {field.label}: Yes
                                        </span>
                                    </div>
                                    <div className="flex items-start flex-0">
                                        <div className="w-4 aspect-square border border-black mr-2 flex-shrink-0 mt-[2px]"></div>
                                        <span className="text-xs leading-snug whitespace-nowrap">
                                            {field.label}: No
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {children &&
                children.map((child, index) => (
                    <div
                        key={index}
                        className="mt-4 w-full"
                        style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                        {child}
                    </div>
                ))}
        </div>
    );
};
